import { useState, useEffect, useRef } from 'react';
import { fetchBatchedOverpassPOIs } from '../services/overpassService';
import { CampingSpot, MapBounds } from '../types';
import { boundsOverlapRatio } from '../utils/boundsUtils';

/**
 * Batched hook that replaces useOSMAmenities + useDumpStations + useCommercialCampsites.
 * Makes a single Overpass query instead of 3 separate ones, reducing latency by ~3 seconds
 * (the global Overpass queue adds 1.5s between each sequential request).
 */
export const useOverpassPOIBatch = (
  activeAmenities: boolean,
  activeDumpStations: boolean,
  activeCampsites: boolean,
  activePlaygrounds: boolean,
  zoom: number,
  currentViewBounds: MapBounds | null,
  onNewSpots: (spots: CampingSpot[]) => void
) => {
  const [isLoading, setIsLoading] = useState(false);
  const [errorAmenities, setErrorAmenities] = useState(false);
  const [errorDump, setErrorDump] = useState(false);
  const [errorCamp, setErrorCamp] = useState(false);
  const [errorPlaygrounds, setErrorPlaygrounds] = useState(false);
  const lastFetchedBoundsRef = useRef<MapBounds | null>(null);
  const prevActiveRef = useRef({ amenities: false, dump: false, camp: false, playgrounds: false });

  const anyActive = activeAmenities || activeDumpStations || activeCampsites || activePlaygrounds;

  useEffect(() => {
    if (!anyActive || zoom < 9 || !currentViewBounds) {
      setIsLoading(false);
      // Reset state when all layers are turned off
      if (!anyActive) {
        setErrorAmenities(false);
        setErrorDump(false);
        setErrorCamp(false);
        setErrorPlaygrounds(false);
        lastFetchedBoundsRef.current = null;
      }
      return;
    }

    // Re-fetch if a previously inactive layer becomes active (user toggled a new layer on)
    const layerBecameActive =
      (activeAmenities && !prevActiveRef.current.amenities) ||
      (activeDumpStations && !prevActiveRef.current.dump) ||
      (activeCampsites && !prevActiveRef.current.camp) ||
      (activePlaygrounds && !prevActiveRef.current.playgrounds);
    prevActiveRef.current = { amenities: activeAmenities, dump: activeDumpStations, camp: activeCampsites, playgrounds: activePlaygrounds };

    if (!layerBecameActive && lastFetchedBoundsRef.current && boundsOverlapRatio(lastFetchedBoundsRef.current, currentViewBounds) > 0.7) {
      return;
    }

    const fetchAll = async () => {
      setIsLoading(true);
      const hasOthers = activeAmenities || activeDumpStations || activePlaygrounds;
      try {
        if (activeCampsites && hasOthers) {
          // Priority: fetch campsites first so they appear before slower combined query
          const { campsites } = await fetchBatchedOverpassPOIs(currentViewBounds, { campsites: true, amenities: false, dumpStations: false, playgrounds: false });
          if (campsites.length > 0) onNewSpots(campsites);
          setErrorCamp(false);
          // Then fetch the rest
          const rest = await fetchBatchedOverpassPOIs(currentViewBounds, { campsites: false, amenities: activeAmenities, dumpStations: activeDumpStations, playgrounds: activePlaygrounds });
          const merged: CampingSpot[] = [];
          if (activeAmenities && rest.amenities.length > 0) merged.push(...rest.amenities);
          if (activeDumpStations && rest.dumpStations.length > 0) merged.push(...rest.dumpStations);
          if (activePlaygrounds && rest.playgrounds.length > 0) merged.push(...rest.playgrounds);
          if (merged.length > 0) onNewSpots(merged);
          setErrorAmenities(false);
          setErrorDump(false);
          setErrorPlaygrounds(false);
        } else {
          const { campsites, amenities, dumpStations, playgrounds } = await fetchBatchedOverpassPOIs(currentViewBounds, { campsites: activeCampsites, amenities: activeAmenities, dumpStations: activeDumpStations, playgrounds: activePlaygrounds });
          const merged: CampingSpot[] = [];
          if (activeCampsites && campsites.length > 0) merged.push(...campsites);
          if (activeAmenities && amenities.length > 0) merged.push(...amenities);
          if (activeDumpStations && dumpStations.length > 0) merged.push(...dumpStations);
          if (activePlaygrounds && playgrounds.length > 0) merged.push(...playgrounds);
          if (merged.length > 0) onNewSpots(merged);
          setErrorAmenities(false);
          setErrorDump(false);
          setErrorCamp(false);
          setErrorPlaygrounds(false);
        }
        lastFetchedBoundsRef.current = currentViewBounds;
      } catch (e) {
        console.error("Failed to fetch batched Overpass POIs", e);
        setErrorAmenities(activeAmenities);
        setErrorDump(activeDumpStations);
        setErrorCamp(activeCampsites);
        setErrorPlaygrounds(activePlaygrounds);
      } finally {
        setIsLoading(false);
      }
    };

    const timer = setTimeout(fetchAll, 1500);
    return () => clearTimeout(timer);
  }, [currentViewBounds, anyActive, activeAmenities, activeDumpStations, activeCampsites, activePlaygrounds, zoom]);

  return {
    isLoading,
    errorOsm: errorAmenities,
    errorDump: errorDump,
    errorCamp: errorCamp,
    errorPlaygrounds,
  };
};
